import type { AnyFormApi, FieldListeners } from '@tanstack/react-form';
import type { ComposedForm } from 'lib-node-question-builder';
import { formIs, or } from 'lib-node-question-builder/utils';

import type { DtoV1Borrower1003BorrowerSummaryAddress } from '~/__generated__/api';
import useIsWriteAccessDisabled from '~/common/hooks/useIsWriteAccessDisabled';
import Box from '~/common/mui/Layout/Box';
import { DeleteFieldArrayRowButton } from '~/pages/Loan1003DataGrids/common/components/FormFieldArrayButtons';
import { READONLY_CONDITION } from '~/pages/Loan1003DataGrids/common/utils/utils';

import type { AddressInfoData, BorrowerDropdownOptions } from '../../../types';
import {
  AddressCard,
  AddressCardActions,
  AddressCardContent,
  AddressCardSeparator,
  AddressCardTitle,
} from './AddressCard';
import { createCommonAddressTemplate } from './commonAddressTemplate';
import {
  SAME_AS_PRIMARY_FIELD,
  syncMailingAddressWithPrimary,
} from './listeners';
import {
  demotePrimaryToAssociatedAddresses,
  getValidAddressId,
} from './primaryAddressSelection';

const PRIMARY_RESIDENCE_FIELD = 'residenceHistoryDetails.primaryResidence';
const PRIMARY_RESIDENCE_SELECTION_FIELD =
  'residenceHistoryDetails.primaryResidenceSelectionId';

type ResidenceHistoryDetails =
  AddressInfoData['formData']['residenceHistoryDetails'];

type PrimaryAddressSelectorProps = {
  form: ComposedForm<AddressInfoData['formData']>;
  dropdownOptions: Omit<BorrowerDropdownOptions, 'joinedBorrowers'>;
  primaryAddressSelectionOptions: DtoV1Borrower1003BorrowerSummaryAddress[];
  initialPrimaryId?: number | null;
};

function clearPrimaryResidence(formApi: AnyFormApi) {
  formApi.setFieldValue('residenceHistoryDetails', (currentDetails) => ({
    ...currentDetails,
    primaryResidence: null,
    primaryResidenceSelectionId: '',
  }));
}

function getPrimaryResidence(formApi: AnyFormApi) {
  const primaryResidence = formApi.getFieldValue(PRIMARY_RESIDENCE_FIELD);

  return primaryResidence as DtoV1Borrower1003BorrowerSummaryAddress | null;
}

type PrimaryAddressChangeListener = NonNullable<
  FieldListeners<any, any, any>['onChange']
>;

function createPrimaryAddressChangeListener(
  primaryAddressSelectionOptions: DtoV1Borrower1003BorrowerSummaryAddress[],
  historyAddressIds: Set<string>,
  initialPrimaryId: number | null,
): PrimaryAddressChangeListener {
  return ({ fieldApi, value }) => {
    if (typeof value === 'string' && historyAddressIds.has(value)) {
      const currentPrimaryResidence = getPrimaryResidence(fieldApi.form);

      fieldApi.form.setFieldValue(
        'residenceHistoryDetails',
        (currentDetails) => ({
          ...currentDetails,
          primaryResidence: currentPrimaryResidence,
          primaryResidenceSelectionId: currentPrimaryResidence
            ? String(currentPrimaryResidence.addressIdentifier?.addressId ?? '')
            : '',
        }),
      );

      return;
    }

    const selectedAddress = primaryAddressSelectionOptions.find(
      (option) => String(option.addressIdentifier?.addressId) === value,
    );
    fieldApi.form.setFieldValue('residenceHistoryDetails', (currentDetails) => {
      const details = currentDetails as ResidenceHistoryDetails;
      const outgoingPrimary = details.primaryResidence;
      const isSameAddressReselected =
        String(outgoingPrimary?.addressIdentifier?.addressId ?? '') === value;
      const shouldDemote =
        initialPrimaryId !== null &&
        getValidAddressId(outgoingPrimary) === initialPrimaryId;

      return {
        ...details,
        borrowerAssociatedAddresses:
          !isSameAddressReselected && shouldDemote
            ? demotePrimaryToAssociatedAddresses(details, outgoingPrimary)
            : details.borrowerAssociatedAddresses,
        primaryResidence: selectedAddress
          ? {
              ...selectedAddress,
              borrowerAddress: {
                ...selectedAddress.borrowerAddress,
                primaryResidence: true,
              },
            }
          : null,
      };
    });

    if (fieldApi.form.getFieldValue(SAME_AS_PRIMARY_FIELD)) {
      syncMailingAddressWithPrimary(fieldApi.form);
    }
  };
}

export function PrimaryAddressSelector({
  form,
  dropdownOptions,
  primaryAddressSelectionOptions,
  initialPrimaryId = null,
}: Readonly<PrimaryAddressSelectorProps>) {
  const isWriteAccessDisabled = useIsWriteAccessDisabled();

  return (
    <Box sx={{ marginBottom: '1.5rem' }}>
      <Box sx={{ width: '60%' }}>
        <form.Subscribe
          selector={(s) =>
            s.values?.residenceHistoryDetails?.borrowerResidenceHistory
          }
        >
          {(borrowerResidenceHistory) => {
            const historyAddressIds = new Set(
              (borrowerResidenceHistory ?? [])
                .map((address) => address.addressIdentifier?.addressId)
                .filter((addressId): addressId is number => addressId != null)
                .map(String),
            );

            const selectionDropdownOptions = primaryAddressSelectionOptions
              .filter(
                (option) =>
                  !historyAddressIds.has(
                    String(option.addressIdentifier?.addressId ?? ''),
                  ),
              )
              .map((option) => ({
                label: option.formattedAddressString ?? '',
                value: String(option.addressIdentifier?.addressId ?? ''),
              }));
            const onPrimaryAddressChange = createPrimaryAddressChangeListener(
              primaryAddressSelectionOptions,
              historyAddressIds,
              initialPrimaryId,
            );

            return (
              <form.Select
                name={PRIMARY_RESIDENCE_SELECTION_FIELD}
                label="Primary Address"
                placeholder="Select Primary Address"
                options={selectionDropdownOptions}
                listeners={{
                  onChange: onPrimaryAddressChange,
                }}
                conditions={{ isDisabled: READONLY_CONDITION }}
              />
            );
          }}
        </form.Subscribe>
      </Box>
      <Box sx={{ padding: '0 0.5rem' }}>
        <form.Subscribe
          selector={(s) =>
            Boolean(s.values?.residenceHistoryDetails?.primaryResidence)
          }
        >
          {(hasPrimaryResidence) =>
            hasPrimaryResidence ? (
              <AddressCard>
                <AddressCardContent width="60%">
                  <AddressCardTitle>Address</AddressCardTitle>
                  <form.Grid
                    name="primaryAddressGrid"
                    columns={4}
                    fields={[
                      ...createCommonAddressTemplate(PRIMARY_RESIDENCE_FIELD),
                      {
                        elementType: 'question',
                        type: 'radio',
                        label: 'Primary Residence',
                        showLabel: false,
                        colStart: 1,
                        span: 4,
                        name: `${PRIMARY_RESIDENCE_FIELD}.borrowerAddress.primaryResidence`,
                        conditions: { isDisabled: READONLY_CONDITION },
                        options: [
                          { label: 'Primary', value: 'true' },
                          { label: 'Previous', value: 'false' },
                        ],
                      },
                    ]}
                  />
                </AddressCardContent>
                <AddressCardSeparator />
                <AddressCardContent width="40%">
                  <AddressCardTitle>Residence Info</AddressCardTitle>
                  <form.Grid name="primaryResidenceLengthGrid" columns={4}>
                    <form.GridItem>
                      <form.Masked
                        name={`${PRIMARY_RESIDENCE_FIELD}.yearsAtResidence`}
                        label="Years"
                        placeholder="Years"
                        pattern="##"
                        rules={{ required: true }}
                        conditions={{ isDisabled: READONLY_CONDITION }}
                      />
                    </form.GridItem>
                    <form.GridItem>
                      <form.Masked
                        name={`${PRIMARY_RESIDENCE_FIELD}.monthsAtResidence`}
                        label="Months"
                        placeholder="Months"
                        pattern="##"
                        rules={{
                          required: true,
                          onBlur: {
                            type: 'number',
                            gte: 0,
                            lte: 11,
                            message: 'Must be between 0 and 11',
                          },
                        }}
                        conditions={{ isDisabled: READONLY_CONDITION }}
                      />
                    </form.GridItem>
                    <form.GridItem colSpan={4}>
                      <form.Radio
                        name={`${PRIMARY_RESIDENCE_FIELD}.ownershipStatus`}
                        label="Ownership Status"
                        showLabel={false}
                        options={dropdownOptions?.ownershipStatus ?? []}
                        rules={{ required: true }}
                        conditions={{ isDisabled: READONLY_CONDITION }}
                      />
                    </form.GridItem>
                    <form.GridItem colSpan={2}>
                      <form.Currency
                        name={`${PRIMARY_RESIDENCE_FIELD}.rentAmount`}
                        label="Rent Amount"
                        placeholder="Rent Amount"
                        rules={{ required: true }}
                        conditions={{
                          isDisabled: READONLY_CONDITION,
                          isVisible: or(
                            formIs(
                              `${PRIMARY_RESIDENCE_FIELD}.ownershipStatus`,
                              '1',
                            ),
                            formIs(
                              `${PRIMARY_RESIDENCE_FIELD}.ownershipStatus`,
                              1,
                            ),
                          ),
                        }}
                      />
                    </form.GridItem>
                  </form.Grid>
                </AddressCardContent>
                <AddressCardActions>
                  <form.Field name={PRIMARY_RESIDENCE_FIELD}>
                    {(field) => (
                      <DeleteFieldArrayRowButton
                        readOnlyMode={isWriteAccessDisabled}
                        onClick={() => {
                          clearPrimaryResidence(field.form);
                        }}
                      />
                    )}
                  </form.Field>
                </AddressCardActions>
              </AddressCard>
            ) : null
          }
        </form.Subscribe>
      </Box>
    </Box>
  );
}
